import { BoxContainer, BoxContainerProps, CircleIcon, FluidCanvas, PrimaryButton, ReactIcons } from "@kalyzee/kalyzee-ui";
import { FullscreenContainer, FullscreenContainerRef } from "@kalyzee/kast-web-sdk/web-components";

import workBoxStyles from "./WorkBox.module.css";
import styles from "./PlayerWorkBox.module.css";
import React, { useCallback, useRef, useState } from "react";
import clsx from "clsx";
import { UserAvatar } from "@kalyzee/kast-web-sdk/web-components";

export interface PlayerWorkBoxProps {
  children: React.ReactNode;
  label: string;
  labelInfo?: string;
  userDisplayName?: string;
  avatar?: string;
  overlayMode?: BoxContainerProps["mode"];
  overlayContent?: React.ReactNode;
  extraHeaderContent?: React.ReactNode;
  className?: string;
  topBarClassName?: string;
  audio?: boolean;
  video?: boolean;
  isMe?: boolean;
  isHighlighted?: boolean;
  isPlayingSound?: boolean;
  isSmall?: boolean;
  nativeProps?: React.HTMLAttributes<HTMLDivElement>;
  idleAnimation?: boolean;
  fullscreenButton?: boolean;
  onChangeAudio?: () => void;
  onChangeVideo?: () => void;
}


export const PlayerWorkBox = React.memo(({
  children,
  label,
  labelInfo,
  userDisplayName,
  avatar,
  overlayMode,
  overlayContent,
  extraHeaderContent,
  className,
  topBarClassName,
  audio,
  video,
  isMe,
  isHighlighted,
  isPlayingSound,
  isSmall,
  nativeProps,
  idleAnimation,
  fullscreenButton,
  onChangeAudio,
  onChangeVideo,
}: PlayerWorkBoxProps) => {
  const fullscreenRef = useRef<FullscreenContainerRef>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const onToggleFullscreen = useCallback(() => {
    if (isFullscreen) fullscreenRef.current?.exit();
    else fullscreenRef.current?.enter();
  }, [isFullscreen]);
  const left = React.useMemo(() => {
    const result: React.ReactNode[] = [];
    if (extraHeaderContent) {
      result.push(extraHeaderContent);
    }
    if (isHighlighted) {
      result.push(<ReactIcons.Hand2 className={styles.nameIcon} />);
    } else if (isMe) {
      result.push(<ReactIcons.Home className={styles.nameIcon} />);
    }
    result.push(<span key="label" className={workBoxStyles.label}>{label}</span>);
    if (labelInfo) {
      result.push(
        <span key="youLabel" className={workBoxStyles.labelInfo}>{labelInfo}</span>
      );
    }
    return result;
  }, [extraHeaderContent, isHighlighted, isMe, label, labelInfo]);

  const right = React.useMemo(() => {
    const result: React.ReactNode[] = [];
    if (video !== undefined) {
      result.push(
        <PrimaryButton
          key="video"
          onClick={onChangeVideo}
        >
          <CircleIcon.Variants.Camera
            className={styles.mediaBtn}
            disabled={!video}
          />
        </PrimaryButton>
      );
    }
    if (audio !== undefined) {
      result.push(
        <PrimaryButton
          key="audio"
          onClick={onChangeAudio}
        >
          <CircleIcon.Variants.Mic
            className={styles.mediaBtn}
            disabled={!audio}
          />
        </PrimaryButton>
      );
    }
    if (fullscreenButton) {
      result.push(
        <PrimaryButton
          key="fullscreen"
          onClick={onToggleFullscreen}
        >
          <CircleIcon.Variants.Fullscreen
            className={styles.mediaBtn}
            active={isFullscreen}
          />
        </PrimaryButton>
      );
    }
    return result;
  }, [video, audio, onChangeVideo, onChangeAudio, fullscreenButton, isFullscreen, onToggleFullscreen]);

  const renderDisabedCameraContent = () => {
    if (video) return null;
    return (
      <div className={styles.disabledCameraOverlay}>
        <UserAvatar 
          mode="image" 
          src={avatar} 
          username={userDisplayName}
          className={styles.avatar} 
        />
      </div>
    );
  }

  const renderIdleAnimation = () => {
    if (!idleAnimation) return null;
    return (
      <div className={styles.idleAnimationContainer}>
        <FluidCanvas speed={0.5} shapeSize="big" />
      </div>
    );
  };
  
  const content = (
    <BoxContainer
      className={clsx({
        [styles.container]: true,
        [styles.highlighted]: isHighlighted,
        [styles.playingSound]: isPlayingSound,
        [styles.fullscreen]: isFullscreen,
        [styles.small]: isSmall,
      }, className)}
      topBarProps={{
        left,
        center: [],
        right: right.length ? right : undefined,
        className: clsx({
          [styles.playerTopBar]: true,
          [styles.transparent]: !video,
        }, topBarClassName),
      }}
      mode={overlayMode}
      overlayContent={isFullscreen ? undefined : overlayContent}
      nativeProps={nativeProps}
    >
      {children}
      {renderDisabedCameraContent()}
      {renderIdleAnimation()}
    </BoxContainer>
  );

  if (fullscreenButton) {
    return (
      <FullscreenContainer
        ref={fullscreenRef}
        useNative
        onFullScreen={setIsFullscreen}
        style={{ width: "100%", height: "100%" }}
      >
        {content}
      </FullscreenContainer>
    );
  }

  return content;
});